import { create } from 'zustand';
import { localMinutesToIso } from './utils';
import type {
  Offering, Lane, Treatment, Appointment,
  AppointmentRequest, CalendarView, TimeJump,
  ComputedWarning,
} from './types';
import type { Guest } from '@/types/Guest';

type ActiveEditor = {
  userId: string;
  userName: string;
};

/** How long (ms) after a commit we still reject WS echoes for that appointment */
const RECENT_COMMIT_TTL = 5_000;

/**
 * Carry over guest display names from what we already have onto an incoming
 * appointment. The REST list endpoint resolves `guestDisplayName` per guest,
 * but the WebSocket SYNC / APPOINTMENT_UPDATED payloads ship a lighter guest
 * shape without it. Without this, a SYNC arriving shortly after load would
 * blank every tile back to the generic "N guests" label. We only fill names
 * that are missing on the incoming copy, so a fresh server-resolved name always
 * wins.
 */
function carryGuestNames(incoming: Appointment, previous: Appointment | undefined): Appointment {
  if (!previous) return incoming;
  const prevNames = new Map<string, string>();
  for (const g of previous.guests) {
    if (g.guestDisplayName) prevNames.set(g.guestId, g.guestDisplayName);
  }
  if (prevNames.size === 0) return incoming;
  let changed = false;
  const guests = incoming.guests.map((g) => {
    if (!g.guestDisplayName && prevNames.has(g.guestId)) {
      changed = true;
      return { ...g, guestDisplayName: prevNames.get(g.guestId) };
    }
    return g;
  });
  return changed ? { ...incoming, guests } : incoming;
}

type SlottingState = {
  offering: Offering | null;
  lanes: Lane[];
  treatments: Treatment[];
  appointments: Appointment[];
  requests: AppointmentRequest[];
  selectedRequestIds: Set<string>;
  calendarView: CalendarView;
  currentDate: string;
  timeJump: TimeJump;
  startTime: number;
  wsConnectionState: 'connected' | 'reconnecting' | 'disconnected';
  activeEditors: Map<string, ActiveEditor>;
  warnings: Map<string, ComputedWarning[]>;
  draggingAppointmentId: string | null;
  selectionMode: boolean;
  selectedAppointmentIds: Set<string>;
  highlightAppointmentId: string | null;
  highlightRequestId: string | null;
  pendingUpdates: Set<string>;
  deferredUpdates: Map<string, Appointment>;
  /** Appointment IDs whose WS cancellation arrived during an in-flight local
   *  update. The removal is applied once the pending update settles, so a
   *  concurrent cancel isn't silently dropped. */
  deferredCancellations: Set<string>;
  /** Tracks appointments that were recently committed via REST.
   *  Maps appointmentId → { updatedAt, expiresAt }.
   *  WS messages with updatedAt <= this value are rejected even after
   *  pendingUpdates is cleared, preventing late-arriving echoes from
   *  overwriting the authoritative REST response. */
  recentCommits: Map<string, { updatedAt: string; expiresAt: number }>;
  draggingRequestId: string | null;
  initialFilterStatus: string | null;
  initialFilterLane: string | null;
  initialFilterTreatment: string | null;
  eventTimezone: string | null;
  /** guestId → caller-visible guest data for status-dot evaluation. Populated
   *  by useStatusDotGuestData ONLY when the offering has status-dot rules; the
   *  name-rendering caches stay separate on purpose (empty-rules zero cost). */
  guestRecords: Map<string, Guest>;
};

type SlottingDerived = {
  isRequestBased: () => boolean;
};

type SlottingActions = {
  setOffering: (offering: Offering) => void;
  setLanes: (lanes: Lane[]) => void;
  setTreatments: (treatments: Treatment[]) => void;
  setAppointments: (appointments: Appointment[]) => void;
  /** Replace appointments while preserving local state for any in-flight (pending) updates.
   *  Use this instead of setAppointments when the data comes from a REST fetch that might
   *  race with an optimistic drag/resize. */
  mergeAppointments: (appointments: Appointment[]) => void;
  setRequests: (requests: AppointmentRequest[]) => void;
  setWarnings: (warnings: Map<string, ComputedWarning[]>) => void;
  selectRequest: (requestId: string) => void;
  deselectRequest: (requestId: string) => void;
  clearSelectedRequests: () => void;
  setCalendarView: (view: CalendarView) => void;
  setCurrentDate: (date: string) => void;
  setTimeJump: (jump: TimeJump) => void;
  setStartTime: (minutes: number) => void;
  setWsConnectionState: (state: 'connected' | 'reconnecting' | 'disconnected') => void;
  setActiveEditor: (appointmentId: string, editor: ActiveEditor | null) => void;
  getAppointmentsForLane: (laneId: string, date: string) => Appointment[];
  getCapacityForSlot: (laneId: string, startDt: string, endDt: string, treatmentId?: string) => { used: number; total: number };
  addAppointment: (appointment: Appointment) => void;
  updateAppointmentInStore: (appointment: Appointment) => void;
  removeAppointmentFromStore: (appointmentId: string) => void;
  updateAppointmentStatus: (appointmentId: string, status: string) => void;
  addRequestToStore: (request: AppointmentRequest) => void;
  updateRequestInStore: (request: AppointmentRequest) => void;
  removeRequestFromStore: (requestId: string) => void;
  setDraggingAppointmentId: (id: string | null) => void;
  setSelectionMode: (mode: boolean) => void;
  toggleAppointmentSelection: (appointmentId: string) => void;
  selectAllAppointments: (ids: string[]) => void;
  clearSelectedAppointments: () => void;
  setHighlightAppointmentId: (id: string | null) => void;
  setHighlightRequestId: (id: string | null) => void;
  addPendingUpdate: (appointmentId: string) => void;
  removePendingUpdate: (appointmentId: string) => void;
  setDeferredUpdate: (appointmentId: string, appointment: Appointment) => void;
  clearDeferredUpdate: (appointmentId: string) => void;
  setDeferredCancellation: (appointmentId: string) => void;
  addRecentCommit: (appointmentId: string, updatedAt: string) => void;
  isRecentlyCommitted: (appointmentId: string, incomingUpdatedAt: string) => boolean;
  /** Atomically apply an optimistic update and mark it as pending.
   *  This prevents WS echoes from sneaking in between the update and the pending flag. */
  applyOptimisticUpdate: (appointmentId: string, appointment: Appointment) => void;
  /** Atomically apply the authoritative server response: update appointment,
   *  clear any deferred WS echo, and record the commit to reject late echoes. */
  applyCommittedUpdate: (appointmentId: string, appointment: Appointment) => void;
  setDraggingRequestId: (id: string | null) => void;
  setEventTimezone: (tz: string | null) => void;
  resetForOffering: () => void;
  setInitialFilters: (status: string | null, lane: string | null, treatment: string | null) => void;
  clearInitialFilters: () => void;
  mergeGuestRecords: (guests: Guest[]) => void;
  patchGuestRecord: (guestId: string, patch: Partial<Guest>) => void;
  clearGuestRecords: () => void;
};

export const useSlottingStore = create<SlottingState & SlottingDerived & SlottingActions>((set, get) => ({
  offering: null,
  lanes: [],
  treatments: [],
  appointments: [],
  requests: [],
  selectedRequestIds: new Set(),
  calendarView: 'day' as CalendarView,
  currentDate: new Date().toISOString().split('T')[0],
  timeJump: 30,
  startTime: 0,
  wsConnectionState: 'disconnected',
  activeEditors: new Map(),
  warnings: new Map(),
  draggingAppointmentId: null,
  selectionMode: false,
  selectedAppointmentIds: new Set(),
  highlightAppointmentId: null,
  highlightRequestId: null,
  pendingUpdates: new Set(),
  deferredUpdates: new Map(),
  deferredCancellations: new Set(),
  recentCommits: new Map(),
  draggingRequestId: null,
  initialFilterStatus: null,
  initialFilterLane: null,
  initialFilterTreatment: null,
  eventTimezone: null,
  guestRecords: new Map(),

  isRequestBased: () => get().offering?.bookingMode === 'REQUEST_BASED',

  setOffering: (offering) => set({ offering }),
  setLanes: (lanes) => set({ lanes }),
  setTreatments: (treatments) => set({ treatments }),
  setAppointments: (appointments) => {
    set({ appointments });
  },
  mergeAppointments: (incomingRaw) => set((state) => {
    const { pendingUpdates, recentCommits } = state;
    // Preserve guest display names the incoming payload may have dropped
    // (WS SYNC ships a lighter guest shape than the REST list endpoint).
    const prevById = new Map(state.appointments.map((a) => [a.appointmentId, a] as const));
    const incoming = incomingRaw.map((a) => carryGuestNames(a, prevById.get(a.appointmentId)));
    // Nothing to protect → fast path
    if (pendingUpdates.size === 0 && recentCommits.size === 0) {
      return { appointments: incoming };
    }
    // Build a map of local appointments that must be preserved
    const protectedIds = new Set<string>();
    pendingUpdates.forEach((id) => protectedIds.add(id));
    const now = Date.now();
    recentCommits.forEach(({ expiresAt }, id) => {
      if (expiresAt > now) protectedIds.add(id);
    });
    if (protectedIds.size === 0) return { appointments: incoming };
    const localByIds = new Map(
      state.appointments
        .filter((a) => protectedIds.has(a.appointmentId))
        .map((a) => [a.appointmentId, a] as const),
    );
    const merged = incoming.map((a) => localByIds.get(a.appointmentId) ?? a);
    // Keep any protected appointments that weren't in the incoming set
    for (const [id, local] of localByIds) {
      if (!incoming.some((a) => a.appointmentId === id)) {
        merged.push(local);
      }
    }
    return { appointments: merged };
  }),
  setRequests: (requests) => set({ requests }),
  setWarnings: (warnings) => set({ warnings }),

  selectRequest: (requestId) => set((state) => {
    if (state.selectedRequestIds.size >= 10) return state;
    const next = new Set(state.selectedRequestIds);
    next.add(requestId);
    return { selectedRequestIds: next };
  }),
  deselectRequest: (requestId) => set((state) => {
    const next = new Set(state.selectedRequestIds);
    next.delete(requestId);
    return { selectedRequestIds: next };
  }),
  clearSelectedRequests: () => set({ selectedRequestIds: new Set() }),
  setCalendarView: (view) => {
    if (typeof window !== 'undefined') localStorage.setItem('slotting-calendar-view', view);
    set({ calendarView: view });
  },
  setCurrentDate: (date) => set({ currentDate: date, highlightAppointmentId: null }),
  setTimeJump: (jump) => {
    if (typeof window !== 'undefined') localStorage.setItem('slotting-time-jump', String(jump));
    set({ timeJump: jump });
  },
  setStartTime: (minutes) => set({ startTime: minutes }),

  setWsConnectionState: (wsConnectionState) => set({ wsConnectionState }),
  setActiveEditor: (appointmentId, editor) => set((state) => {
    const next = new Map(state.activeEditors);
    if (editor) next.set(appointmentId, editor);
    else next.delete(appointmentId);
    return { activeEditors: next };
  }),

  getAppointmentsForLane: (laneId, date) => {
    const state = get();
    const tz = state.offering?.timezone ?? state.eventTimezone ?? 'UTC';
    // Day bounds anchored in the EVENT timezone, not UTC. Using UTC midnight put
    // appointments near local midnight on the wrong calendar day for non-UTC events.
    const dayStart = localMinutesToIso(date, 0, tz);
    const dayEnd = localMinutesToIso(date, 24 * 60, tz);
    return state.appointments.filter(a =>
      a.laneIds.includes(laneId) &&
      a.startDt < dayEnd &&
      a.endDt > dayStart &&
      a.status !== 'CANCELLED'
    );
  },

  getCapacityForSlot: (laneId, startDt, endDt, treatmentId) => {
    const { appointments, treatments } = get();
    const overlapping = appointments.filter(a =>
      a.laneIds.includes(laneId) &&
      a.startDt < endDt &&
      a.endDt > startDt &&
      a.status !== 'CANCELLED'
    );
    const used = overlapping.reduce((sum, a) => sum + a.guests.length, 0);
    const treatment = treatmentId ? treatments.find(t => t.treatmentId === treatmentId) : undefined;
    return { used, total: treatment?.capacity ?? 1 };
  },

  addAppointment: (appointment) => set((state) => {
    // Deduplicate — WS and optimistic add can both fire
    if (state.appointments.some(a => a.appointmentId === appointment.appointmentId)) {
      return { appointments: state.appointments.map(a => a.appointmentId === appointment.appointmentId ? carryGuestNames(appointment, a) : a) };
    }
    return { appointments: [...state.appointments, appointment] };
  }),

  updateAppointmentInStore: (appointment) => set((state) => {
    const current = state.appointments.find(a => a.appointmentId === appointment.appointmentId);
    if (!current) {
      console.warn('[SlottingStore] updateAppointmentInStore: ID not found, adding:', appointment.appointmentId);
      return { appointments: [...state.appointments, appointment] };
    }
    // Keep guest names the WS update payload dropped (see carryGuestNames).
    const next = carryGuestNames(appointment, current);
    return {
      appointments: state.appointments.map(a =>
        a.appointmentId === appointment.appointmentId ? next : a
      ),
    };
  }),

  removeAppointmentFromStore: (appointmentId) => set((state) => ({
    appointments: state.appointments.filter(a => a.appointmentId !== appointmentId),
  })),

  updateAppointmentStatus: (appointmentId, status) => set((state) => ({
    appointments: state.appointments.map(a =>
      a.appointmentId === appointmentId ? { ...a, status: status as Appointment['status'] } : a
    ),
  })),

  // Prepend, but idempotent: if a request with this id is already loaded, replace
  // it in place instead of adding a second copy. The active lane is fed from both
  // paginated loads and optimistic inserts (create / unarchive / search-result
  // merge), which can otherwise race into a duplicate card + duplicate calendar
  // overlay for the same request.
  addRequestToStore: (request) => set((state) =>
    state.requests.some((r) => r.appointmentRequestId === request.appointmentRequestId)
      ? { requests: state.requests.map((r) => r.appointmentRequestId === request.appointmentRequestId ? request : r) }
      : { requests: [request, ...state.requests] },
  ),

  updateRequestInStore: (request) => set((state) => ({
    requests: state.requests.map(r =>
      r.appointmentRequestId === request.appointmentRequestId ? request : r
    ),
  })),

  removeRequestFromStore: (requestId) => set((state) => ({
    requests: state.requests.filter(r => r.appointmentRequestId !== requestId),
  })),

  setDraggingAppointmentId: (id) => set({ draggingAppointmentId: id }),

  setSelectionMode: (mode) => set({ selectionMode: mode, selectedAppointmentIds: new Set() }),
  toggleAppointmentSelection: (appointmentId) => set((state) => {
    const next = new Set(state.selectedAppointmentIds);
    if (next.has(appointmentId)) next.delete(appointmentId);
    else next.add(appointmentId);
    return { selectedAppointmentIds: next };
  }),
  selectAllAppointments: (ids) => set({ selectedAppointmentIds: new Set(ids) }),
  clearSelectedAppointments: () => set({ selectedAppointmentIds: new Set() }),
  setHighlightAppointmentId: (id) => set({ highlightAppointmentId: id }),
  setHighlightRequestId: (id) => set({ highlightRequestId: id }),
  addPendingUpdate: (appointmentId) => set((state) => {
    const next = new Set(state.pendingUpdates);
    next.add(appointmentId);
    return { pendingUpdates: next };
  }),
  removePendingUpdate: (appointmentId) => set((state) => {
    const next = new Set(state.pendingUpdates);
    next.delete(appointmentId);
    // A cancellation that arrived while the update was in-flight wins: drop the
    // appointment now instead of waiting for the next SYNC.
    if (state.deferredCancellations.has(appointmentId)) {
      const nextCancellations = new Set(state.deferredCancellations);
      nextCancellations.delete(appointmentId);
      const nextDeferred = new Map(state.deferredUpdates);
      nextDeferred.delete(appointmentId);
      return {
        pendingUpdates: next,
        deferredCancellations: nextCancellations,
        deferredUpdates: nextDeferred,
        appointments: state.appointments.filter(a => a.appointmentId !== appointmentId),
      };
    }
    // Auto-flush any deferred WS update for this appointment
    const deferred = state.deferredUpdates.get(appointmentId);
    if (deferred) {
      const nextDeferred = new Map(state.deferredUpdates);
      nextDeferred.delete(appointmentId);
      const current = state.appointments.find(a => a.appointmentId === appointmentId);
      // Only apply deferred if it's strictly newer than what we have
      if (!current?.updatedAt || !deferred.updatedAt || deferred.updatedAt > current.updatedAt) {
        return {
          pendingUpdates: next,
          deferredUpdates: nextDeferred,
          appointments: state.appointments.some(a => a.appointmentId === appointmentId)
            ? state.appointments.map(a => a.appointmentId === appointmentId ? deferred : a)
            : [...state.appointments, deferred],
        };
      }
      return { pendingUpdates: next, deferredUpdates: nextDeferred };
    }
    return { pendingUpdates: next };
  }),
  setDeferredUpdate: (appointmentId, appointment) => set((state) => {
    const nextDeferred = new Map(state.deferredUpdates);
    nextDeferred.set(appointmentId, appointment);
    return { deferredUpdates: nextDeferred };
  }),
  clearDeferredUpdate: (appointmentId) => set((state) => {
    const next = new Map(state.deferredUpdates);
    next.delete(appointmentId);
    return { deferredUpdates: next };
  }),
  setDeferredCancellation: (appointmentId) => set((state) => {
    const next = new Set(state.deferredCancellations);
    next.add(appointmentId);
    return { deferredCancellations: next };
  }),
  addRecentCommit: (appointmentId, updatedAt) => set((state) => {
    const next = new Map(state.recentCommits);
    next.set(appointmentId, { updatedAt, expiresAt: Date.now() + RECENT_COMMIT_TTL });
    return { recentCommits: next };
  }),
  isRecentlyCommitted: (appointmentId, incomingUpdatedAt) => {
    const entry = get().recentCommits.get(appointmentId);
    if (!entry) return false;
    if (Date.now() > entry.expiresAt) return false;
    // Reject if the incoming updatedAt is not strictly newer than our committed version
    return incomingUpdatedAt <= entry.updatedAt;
  },
  applyOptimisticUpdate: (appointmentId, appointment) => set((state) => {
    const nextPending = new Set(state.pendingUpdates);
    nextPending.add(appointmentId);
    const exists = state.appointments.some(a => a.appointmentId === appointmentId);
    return {
      appointments: exists
        ? state.appointments.map(a => a.appointmentId === appointmentId ? appointment : a)
        : [...state.appointments, appointment],
      pendingUpdates: nextPending,
    };
  }),
  applyCommittedUpdate: (appointmentId, appointment) => set((state) => {
    const nextDeferred = new Map(state.deferredUpdates);
    nextDeferred.delete(appointmentId);
    const nextRecent = new Map(state.recentCommits);
    if (appointment.updatedAt) {
      nextRecent.set(appointmentId, { updatedAt: appointment.updatedAt, expiresAt: Date.now() + RECENT_COMMIT_TTL });
    }
    const exists = state.appointments.some(a => a.appointmentId === appointmentId);
    return {
      appointments: exists
        ? state.appointments.map(a => a.appointmentId === appointmentId ? appointment : a)
        : [...state.appointments, appointment],
      deferredUpdates: nextDeferred,
      recentCommits: nextRecent,
    };
  }),
  setDraggingRequestId: (id) => set({ draggingRequestId: id }),
  setEventTimezone: (tz) => set({ eventTimezone: tz }),
  resetForOffering: () => set({
    appointments: [],
    lanes: [],
    treatments: [],
    requests: [],
    selectedRequestIds: new Set<string>(),
    selectedAppointmentIds: new Set<string>(),
    activeEditors: new Map(),
    warnings: new Map(),
    pendingUpdates: new Set<string>(),
    deferredUpdates: new Map<string, Appointment>(),
    deferredCancellations: new Set<string>(),
    recentCommits: new Map(),
    highlightAppointmentId: null,
    highlightRequestId: null,
    draggingAppointmentId: null,
    selectionMode: false,
    currentDate: new Date().toISOString().split('T')[0],
    draggingRequestId: null,
    initialFilterStatus: null,
    initialFilterLane: null,
    initialFilterTreatment: null,
    guestRecords: new Map<string, Guest>(),
  }),
  setInitialFilters: (status, lane, treatment) => set({
    initialFilterStatus: status,
    initialFilterLane: lane,
    initialFilterTreatment: treatment,
  }),
  clearInitialFilters: () => set({
    initialFilterStatus: null,
    initialFilterLane: null,
    initialFilterTreatment: null,
  }),

  mergeGuestRecords: (guests) => set((state) => {
    if (guests.length === 0) return state;
    const next = new Map(state.guestRecords);
    for (const g of guests) {
      if (g.guestId) next.set(g.guestId, g);
    }
    return { guestRecords: next };
  }),
  // guestDataUpdated patches are shallow except additionalFields, which the
  // event ships as a one-level merge — mirror that here so a patched record
  // keeps the additional fields the patch didn't touch.
  patchGuestRecord: (guestId, patch) => set((state) => {
    const current = state.guestRecords.get(guestId);
    if (!current) return state;
    const next = new Map(state.guestRecords);
    next.set(guestId, {
      ...current,
      ...patch,
      additionalFields: patch.additionalFields
        ? { ...current.additionalFields, ...patch.additionalFields }
        : current.additionalFields,
    });
    return { guestRecords: next };
  }),
  clearGuestRecords: () => set((state) =>
    state.guestRecords.size === 0 ? state : { guestRecords: new Map() },
  ),
}));

export const selectTimezone = (s: SlottingState & SlottingDerived & SlottingActions) =>
  s.offering?.timezone ?? s.eventTimezone ?? "UTC";
